// useDemoRefSubmit.ts — playground-signup-card cycle (THI-33, 2026-08-22 spec §B).
import { useEffect } from 'react';
import type { DemoRefResultDto } from '@repo/shared-types';
import { apiFetch } from '../lib/api';
import { captureDemoRef, clearDemoRef, readDemoRef } from '../lib/demo-ref';

/** Module-level, keyed by demo id — not a single boolean (fix round 1, medium finding): a bare
 * latch drops a SECOND user's ref after an in-tab sign-out → sign-in, because the first
 * visitor's post already flipped it and the new session's own id would never fire. Keying by
 * id means a DIFFERENT id is always attempted once per page load, while the SAME id is still
 * only ever posted once — a `useRef` wouldn't do either: this must survive across
 * StrictMode's double-invoked effect AND across `Protected` re-mounting (e.g. impersonation)
 * within the same page load. */
const attempted = new Set<string>();

/** Test-only escape hatch — lets `useDemoRefSubmit.test.tsx` start each test with a fresh
 * latch without forcing a new module instance via cache-busting imports. NOT for production
 * use. */
export function __resetDemoRefLatchForTests(): void {
  attempted.clear();
}

/** Fires at most once per demo id per page load, once a session exists: re-captures `?demo=`
 * from the current URL (the Google `callbackURL` round trip lands here), then POSTs the
 * stored id and clears it. A plain `useEffect` + module-level latch rather than a mutation —
 * nothing in the UI depends on the result, and React 19 StrictMode double-invokes effects. */
export function useDemoRefSubmit(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    captureDemoRef(window.location.search);
    const demoId = readDemoRef();
    if (!demoId || attempted.has(demoId)) return;
    attempted.add(demoId);
    apiFetch<DemoRefResultDto>('/api/me/demo-ref', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ demoId }),
    })
      // No user-facing surface for this — attribution is best-effort telemetry, never a
      // sign-up blocker. Swallow any failure (`ApiError` on a 4xx/5xx, or a network error).
      .catch(() => {})
      .finally(() => clearDemoRef());
  }, [enabled]);
}
